import { useCallback, useEffect, useRef, useState } from 'react';
import { PrimaryButton, SecondaryButton, TextButton } from '../components/Button';
import { LiveRegion } from '../components/Screen';
import { copy } from '../copy';
import { ScanDedupe, createDetector, type CodeDetector } from '../lib/scanner';
import { loadCounts } from '../state/route';
import { handleScannedCode } from '../state/scanning';
import { selectCurrentStop, useAppStore, type ManualEntryReason } from '../state/store';

const SLOW_HINT_MS = 15_000;

type Phase = 'explain' | 'starting' | 'scanning' | 'failed';

/** F1/F7/F8/F9 — Scanner. Handmatig invoeren is vanuit elke staat één tik weg. */
export function ScannerScreen() {
  const route = useAppStore((s) => s.route);
  const purpose = useAppStore((s) => s.scanPurpose);
  const stopId = useAppStore((s) => s.stopId);
  const current = useAppStore(selectCurrentStop);
  const prefs = useAppStore((s) => s.prefs);
  const setPrefs = useAppStore((s) => s.setPrefs);
  const navigate = useAppStore((s) => s.navigate);
  const setScanOutcome = useAppStore((s) => s.setScanOutcome);
  const setManualEntryReason = useAppStore((s) => s.setManualEntryReason);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const detectorRef = useRef<CodeDetector | null>(null);
  const dedupeRef = useRef(new ScanDedupe());
  const busyRef = useRef(false);

  const [phase, setPhase] = useState<Phase>(prefs.cameraPermissionAsked ? 'starting' : 'explain');
  const [showSlowHint, setShowSlowHint] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [flash, setFlash] = useState(false);

  const stop = route?.stops.find((candidate) => candidate.id === stopId) ?? current;
  const counts = route ? loadCounts(route) : null;

  // De reden gaat mee naar het andere scherm: dit scherm is weg op het moment dat hij
  // getoond zou moeten worden (F7).
  const goManual = useCallback(
    (reason: ManualEntryReason = null) => {
      setManualEntryReason(reason);
      navigate('manual');
    },
    [navigate, setManualEntryReason],
  );

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    detectorRef.current?.dispose();
    detectorRef.current = null;
  }, []);

  const onCode = useCallback(
    async (code: string) => {
      if (busyRef.current) return;
      if (!dedupeRef.current.accept(code)) return;
      busyRef.current = true;
      try {
        const outcome = await handleScannedCode(code);
        if (outcome.kind === 'loaded') {
          // Bij laden blijft de scanner open: korte mint flash en door met het volgende pakket.
          setFlash(true);
          window.setTimeout(() => setFlash(false), 220);
          return;
        }
        setScanOutcome(outcome);
        navigate('scanresult');
      } finally {
        busyRef.current = false;
      }
    },
    [navigate, setScanOutcome],
  );

  useEffect(() => {
    if (phase !== 'starting') return;
    let cancelled = false;

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;

        const track = stream.getVideoTracks()[0];
        const capabilities = track?.getCapabilities?.() as { torch?: boolean } | undefined;
        setTorchAvailable(Boolean(capabilities?.torch));

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        detectorRef.current = await createDetector();
        if (!cancelled) setPhase('scanning');
      } catch (error) {
        if (cancelled) return;
        const denied =
          error instanceof DOMException &&
          (error.name === 'NotAllowedError' || error.name === 'SecurityError');
        setPhase('failed');
        goManual(denied ? 'cameraDenied' : 'cameraFailed');
      }
    };

    void start();
    return () => {
      cancelled = true;
    };
  }, [phase, goManual, setPrefs]);

  useEffect(() => {
    if (phase !== 'scanning') return;
    let active = true;
    const tick = async () => {
      const video = videoRef.current;
      const detector = detectorRef.current;
      if (active && video && detector && video.readyState >= 2) {
        try {
          const codes = await detector.detect(video);
          if (codes[0]) await onCode(codes[0]);
        } catch {
          /* een frame dat niet leest is geen fout — stil doorscannen */
        }
      }
      if (active) timer = window.setTimeout(() => void tick(), 220);
    };
    let timer = window.setTimeout(() => void tick(), 220);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [phase, onCode]);

  useEffect(() => {
    if (phase !== 'scanning') return;
    const timer = window.setTimeout(() => setShowSlowHint(true), SLOW_HINT_MS);
    return () => window.clearTimeout(timer);
  }, [phase]);

  useEffect(() => stopCamera, [stopCamera]);

  const toggleTorch = () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    const next = !torchOn;
    void track
      .applyConstraints({ advanced: [{ torch: next } as MediaTrackConstraintSet] })
      .then(() => setTorchOn(next))
      .catch(() => undefined);
  };

  if (phase === 'explain') {
    return (
      <div className="screen" data-screen="scanner-permission">
        <div className="screen__body screen__body--center pad-x">
          <p className="t-body o-75">{copy.scanner.permissionExplain}</p>
        </div>
        <div className="screen__foot">
          <PrimaryButton
            onClick={() => {
              // Uitleggen doen we één keer, ook als de systeempopup daarna wordt geweigerd.
              void setPrefs({ cameraPermissionAsked: true });
              setPhase('starting');
            }}
          >
            {copy.scanner.permissionAllow}
          </PrimaryButton>
          <TextButton center onClick={() => goManual()}>
            {copy.scanner.permissionManual}
          </TextButton>
        </div>
      </div>
    );
  }

  return (
    <div className="screen" data-screen="scanner" style={{ background: 'var(--scanner-bg)' }}>
      <div className="row row--between pad-x" style={{ paddingTop: 14 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            {purpose === 'load' ? copy.load.title : (stop?.partnerName ?? '')}
          </div>
          <div className="t-caption" style={{ color: 'var(--text-65)' }}>
            {purpose === 'load'
              ? counts
                ? copy.load.counter(counts.loaded, counts.total)
                : copy.scanner.subtitleLoad
              : copy.scanner.subtitle(stop?.stopOrder ?? 0)}
          </div>
        </div>
        <TextButton onClick={() => navigate(purpose === 'load' ? 'load' : 'stop')}>
          {copy.scanner.close}
        </TextButton>
      </div>

      <div
        className="screen__body"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div
          style={{
            position: 'relative',
            width: 264,
            height: 264,
            maxWidth: '80vw',
            maxHeight: '40vh',
          }}
        >
          <video
            ref={videoRef}
            playsInline
            muted
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 14,
              opacity: phase === 'scanning' ? 1 : 0,
            }}
          />
          {phase === 'starting' ? (
            <div className="skeleton" style={{ position: 'absolute', inset: 0, borderRadius: 14 }} />
          ) : null}
          {flash ? (
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: 14,
                background: 'var(--color-mint)',
                opacity: 0.55,
              }}
            />
          ) : null}
          {(
            [
              { top: 0, left: 0, radius: '14px 0 0 0', borders: ['Top', 'Left'] },
              { top: 0, right: 0, radius: '0 14px 0 0', borders: ['Top', 'Right'] },
              { bottom: 0, left: 0, radius: '0 0 0 14px', borders: ['Bottom', 'Left'] },
              { bottom: 0, right: 0, radius: '0 0 14px 0', borders: ['Bottom', 'Right'] },
            ] as const
          ).map((corner, index) => (
            <span
              key={index}
              aria-hidden
              style={{
                position: 'absolute',
                width: 44,
                height: 44,
                borderRadius: corner.radius,
                top: 'top' in corner ? corner.top : undefined,
                bottom: 'bottom' in corner ? corner.bottom : undefined,
                left: 'left' in corner ? corner.left : undefined,
                right: 'right' in corner ? corner.right : undefined,
                ...Object.fromEntries(
                  corner.borders.map((side) => [`border${side}`, '3px solid var(--color-cream)']),
                ),
              }}
            />
          ))}
          <span
            className="t-caption"
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%,-50%)',
              width: 200,
              textAlign: 'center',
              color: 'var(--text-55)',
            }}
          >
            {phase === 'scanning' ? copy.scanner.hint : ''}
          </span>
        </div>
      </div>

      {showSlowHint ? (
        <p className="t-caption o-65 pad-x" style={{ textAlign: 'center' }}>
          {copy.scanner.slowHint}
        </p>
      ) : null}
      <LiveRegion>
        {purpose === 'load' && counts ? copy.load.counter(counts.loaded, counts.total) : ''}
      </LiveRegion>

      <div className="screen__foot">
        <div className="row">
          {torchAvailable ? (
            <div style={{ flex: 1 }}>
              <SecondaryButton variant="scanner" onClick={toggleTorch}>
                {copy.scanner.torch}
              </SecondaryButton>
            </div>
          ) : null}
          <div style={{ flex: 1 }}>
            <SecondaryButton variant="scanner" onClick={() => goManual()}>
              {copy.scanner.manual}
            </SecondaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}
